import React, { useCallback, useEffect, useState } from "react";
import ModalContainer from "../ModalContainer";
import { EmailField, NameField, PhoneField } from "../../form/Fields";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { zodContestSubmit } from "@/libs/zod/zodValidation";
import styled from "@emotion/styled";
import { CustomTextArea, TextInput } from "../../form";
import {
  SHORT_FORM_ACCOUNT_PH,
  SHORT_FORM_VIDEO_LINK_PH,
} from "@/constants/placeholderTexts";
import { Flex, Popconfirm } from "antd";
import { PrimaryButton } from "../../buttons";
import { color, image } from "@/theme";
import { CommonDescriptionTwo, CommonTitleFour } from "../../fonts/Fonts";
import { mq } from "@/libs/react-responsive/mediaQuery";
import { useMutation } from "@tanstack/react-query";
import { CONTEST_65_SECONDS_PARTICIPATE_API_URL } from "@/constants/apiUrls";
import axios from "axios";
import { SUCCESS_CODE } from "@/constants/responseResults";
import { replaceAllDash } from "@/utils/Functions";
import CampaignCheckBoxGroup from "./CampaignCheckBoxGroup";
import CampaignCheckBoxGroupTwo from "./CampaignCheckBoxGroupTwo";

const Container = styled.form(() => ({
  display: "flex",
  maxWidth: "50rem",
  flexDirection: "column",
  gap: "1rem",
  margin: "0 auto",
}));

const PageTitle = styled(CommonTitleFour)(() => ({
  margin: "2rem 0",
  textAlign: "center",
}));

const SubmitButtonWrapper = styled.div(() => ({
  display: "flex",
  justifyContent: "center",
  marginBlock: "6rem",
}));

const SuccessImage = styled.img(() => ({
  width: "100%",
  maxWidth: "24rem",

  [mq("tablet")]: {
    maxWidth: "30rem",
  },
}));

const ShortFormCampaignFormModal = ({ onCancel, modalOpen }) => {
  const [checkSNS, setCheckSNS] = useState({
    yes: false,
    no: true,
  });

  const [contactPath, setContactPath] = useState([
    { name: "위비티", value: true },
    { name: "씽굿", value: false },
    { name: "올콘", value: false },
    { name: "스펙토리", value: false },
    { name: "씽유", value: false },
    { name: "기타", value: false },
  ]);

  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(zodContestSubmit),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      participant_email: "",
      participant_name: "",
      participant_phone: "",
      participant_account: "",
      sns_subscribe_yn: false,
      sns_link: "",
      contact_path: "",
      contact_path_etc: "",
      video_link: "",
      video_description: "",
      privacy_policy: false,
      is_ai: false,
      ai_tool: "",
      is_submit_other_contest: false,
      other_contest_name: "",
      is_agree_other_contest: true,
      is_agree_copyright: true,
    },
  });

  // NOTE: useState로 관리되는 체크 항목들을 폼 필드로 동기화 (zod 검증용)
  useEffect(() => {
    setValue("sns_subscribe_yn", checkSNS.yes);
  }, [checkSNS.yes, setValue]);

  useEffect(() => {
    const selected = contactPath.find(c => c.value)?.name ?? "";
    setValue("contact_path", selected);
  }, [contactPath, setValue]);

  const { mutate: participateContest } = useMutation({
    mutationFn: async data => {
      return await axios.post(CONTEST_65_SECONDS_PARTICIPATE_API_URL, data);
    },
    onSuccess: data => {
      if (data?.data?.code === SUCCESS_CODE) {
        setSubmitSuccess(true);
        setConfirmLoading(false);
        reset();
        setCheckSNS({
          yes: false,
          no: true,
        });
      }
    },
    onError: error => {
      console.log(error);
      setConfirmLoading(false);
    },
  });

  const onSubmit = useCallback(
    data => {
      const entrySource =
        data.contact_path === "기타" && data.contact_path_etc
          ? `기타(${data.contact_path_etc})`
          : data.contact_path;

      const payload = {
        participant_name: data.participant_name,
        participant_phone: replaceAllDash(data.participant_phone),
        participant_email: data.participant_email,
        savetheearth_email: data.participant_account,
        is_sns_subscribed: data.sns_subscribe_yn,
        sns_link: data.sns_link || "",
        entry_source: entrySource,
        video_link: data.video_link,
        // curl 예시의 키명이 `video_descripton`(오탈자)로 내려와 그대로 맞춤
        video_description: data.video_description || "",
        is_ai: data.is_ai,
        tools: data.is_ai ? data.ai_tool : "",
        is_submit_other_contest: data.is_submit_other_contest,
        other_contest_name: data.is_submit_other_contest
          ? data.other_contest_name
          : null,
        is_agree_other_contest: data.is_agree_other_contest,
        is_agree_copyright: data.is_agree_copyright,
        is_personal_data_consent: data.privacy_policy,
      };

      participateContest(payload);
    },
    [participateContest],
  );

  const showPopconfirm = async () => {
    const output = await trigger(undefined, { shouldFocus: true });

    if (!output) return;

    setOpen(true);
  };

  const handleOk = () => {
    setConfirmLoading(true);
    // 폼 제출 실행
    handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const prevScrollTop = React.useRef(0);

  useEffect(() => {
    const modalElement = document.querySelector(".ant-modal-content");

    const handleScroll = () => {
      if (open && modalElement) {
        const currentScrollTop = modalElement.scrollTop;
        if (Math.abs(currentScrollTop - prevScrollTop.current) >= 50) {
          setOpen(false);
        }
      }
    };

    if (modalElement) {
      prevScrollTop.current = modalElement.scrollTop;
      modalElement.addEventListener("scroll", handleScroll);
      return () => {
        modalElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [open]);

  return (
    <ModalContainer
      width={submitSuccess ? "50rem" : "80vw"}
      open={modalOpen}
      onCancel={() => {
        onCancel();
        setSubmitSuccess(false);
        setOpen(false);
        reset();
      }}
      footer={null}
    >
      <div>
        <Container>
          <PageTitle>65초 공모전</PageTitle>

          {submitSuccess ? (
            <Flex
              align="center"
              justify="center"
              gap="6rem"
              vertical
              style={{ paddingBlock: "3rem" }}
            >
              <SuccessImage
                src={image.declarationEstablishment}
                alt="지원이 완료되었습니다."
              />

              <CommonDescriptionTwo>
                지원이 완료되었습니다.
              </CommonDescriptionTwo>
            </Flex>
          ) : (
            <div>
              <NameField
                name="participant_name"
                control={control}
                required
                placeholder="팀으로 참여하는 경우 대표자 1인"
                customMessage="팀으로 참여하는 경우 대표자 1인"
              />

              <PhoneField
                name="participant_phone"
                control={control}
                placeholder="(번호만 입력 / 예: 01012345678)"
                required
                setValue={setValue}
              />

              <EmailField name="participant_email" control={control} required />

              <TextInput
                control={control}
                name="participant_account"
                placeholder={SHORT_FORM_ACCOUNT_PH}
                label="세이브더얼스 홈페이지 회원 가입 계정 정보"
                required
                maxLength={100}
              />

              <CampaignCheckBoxGroup
                control={control}
                checkSNS={checkSNS}
                setCheckSNS={setCheckSNS}
                errors={errors}
                contactPath={contactPath}
                setContactPath={setContactPath}
              />

              <TextInput
                control={control}
                required
                name="video_link"
                placeholder={SHORT_FORM_VIDEO_LINK_PH}
                label="출품 영상을 게시한 SNS 주소 링크"
                inputMode="url"
                maxLength={300}
                customMessage={
                  <div>
                    <p>
                      유튜브, 인스타그램, 틱톡, 블로그 등 영상을 확인할 수 있는
                      SNS 링크
                    </p>

                    <div style={{ color: "red" }}>
                      <p>
                        ※ SNS 계정은 반드시 '전체 공개' 상태여야 하며, '비공개'
                        계정은 심사 대상에서 제외됩니다.
                      </p>

                      <p>
                        출품 영상 등록 시 필수 해시태그 : #세이브더얼스
                        #세이브더얼스공모전 #지구를지켜줘 #savetheearth
                      </p>
                    </div>
                  </div>
                }
              />
              <CustomTextArea
                control={control}
                name="video_description"
                label="작품명 / 작품 설명"
                placeholder="작품명 / 작품 설명을 입력해 주세요."
                customMessage={
                  <div>
                    <p>출품작에 대해 100자 내외의 간략한 설명</p>

                    <p style={{ color: "red" }}>
                      *작품명 필수 / 간단한 작품소개 필수
                    </p>
                  </div>
                }
              />

              <CampaignCheckBoxGroupTwo control={control} watch={watch} />

              <SubmitButtonWrapper justify="center">
                <Popconfirm
                  okText="확인"
                  cancelText="취소"
                  placement="top"
                  title="65초 공모전 지원하기"
                  description={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <p>해당 지원은 수정이 불가능합니다.</p>

                      <p>이대로 지원하시겠습니까?</p>
                    </div>
                  }
                  icon={false}
                  open={open}
                  onConfirm={handleOk}
                  okButtonProps={{ loading: confirmLoading }}
                  onCancel={handleCancel}
                  zIndex={5001}
                  autoFocus={false}
                >
                  <PrimaryButton
                    bgColor={color.primary02}
                    clickEvent={showPopconfirm}
                  >
                    제출하기
                  </PrimaryButton>
                </Popconfirm>
              </SubmitButtonWrapper>
            </div>
          )}
        </Container>
      </div>
    </ModalContainer>
  );
};

export default ShortFormCampaignFormModal;
